import type { ComponentType } from "react";
import { Navigate, createBrowserRouter } from "react-router";
import { SearchLg } from "@untitledui/icons";
import { RouteStub } from "@mfa-aff/ui";
import { PanelGate } from "./layout/PanelGate";
import { PartnerAuthLayout, PartnerLayout } from "./layout/PartnerLayout";
import { HomePage } from "./pages/HomePage";
import { HowItWorksPage } from "./pages/HowItWorksPage";

/** Route-level code splitting: each page module exports a named component. */
const page = <M,>(load: () => Promise<M>, pick: (m: M) => ComponentType) => async () => ({ Component: pick(await load()) });

export const router = createBrowserRouter(
    [
        // Public program page: no sign-in, and outside PanelGate so it shows even while the panel is closed.
        { path: "program", lazy: page(() => import("./pages/program/ProgramPage"), (m) => m.ProgramRoute) },
        {
            // Owner open/closed switch first: closed → «در حال آماده‌سازی» screen for every route.
            element: <PanelGate />,
            children: [
                {
                    element: <PartnerAuthLayout />,
                    children: [
                        { path: "login", lazy: page(() => import("./pages/auth/LoginPage"), (m) => m.LoginPage) },
                        // Landing of the my.mizbanfa.net «بازاریابی» menu: no button, straight on.
                        { path: "enter", lazy: page(() => import("./pages/auth/EnterPage"), (m) => m.EnterPage) },
                        { path: "join", lazy: page(() => import("./pages/auth/JoinPage"), (m) => m.JoinPage) },
                        // There is no separate signup any more (WHMCS is the only identity); old links go to sign-in.
                        { path: "signup", element: <Navigate to="/login" replace /> },
                        { path: "auth/callback", lazy: page(() => import("./pages/auth/AuthCallbackPage"), (m) => m.AuthCallbackPage) },
                        // SMS owner check of a Mizbanfa client account with several users.
                        { path: "auth/verify-owner", lazy: page(() => import("./pages/auth/VerifyOwnerPage"), (m) => m.VerifyOwnerPage) },
                        // Older API builds redirect here (`?result=`); same page.
                        { path: "login/callback", lazy: page(() => import("./pages/auth/AuthCallbackPage"), (m) => m.AuthCallbackPage) },
                    ],
                },
                {
                    element: <PartnerLayout />,
                    children: [
                        { index: true, element: <HomePage /> },
                        { path: "how-it-works", element: <HowItWorksPage /> },
                        { path: "codes", lazy: page(() => import("./pages/codes/CodesPage"), (m) => m.CodesPage) },
                        { path: "links", lazy: page(() => import("./pages/links/LinksPage"), (m) => m.LinksPage) },
                        { path: "refcode", lazy: page(() => import("./pages/refcode/RefCodePage"), (m) => m.RefCodePage) },
                        { path: "wordpress", lazy: page(() => import("./pages/wordpress/WordpressPage"), (m) => m.WordpressPage) },
                        { path: "links/stats", lazy: page(() => import("./pages/links/LinkStatsPage"), (m) => m.LinkStatsPage) },
                        { path: "orders", lazy: page(() => import("./pages/orders/OrdersPage"), (m) => m.OrdersPage) },
                        { path: "wallet", lazy: page(() => import("./pages/wallet/WalletPage"), (m) => m.WalletPage) },
                        { path: "wallet/receipts/:id", lazy: page(() => import("./pages/wallet/ReceiptPage"), (m) => m.ReceiptPage) },
                        { path: "earnings", lazy: page(() => import("./pages/earnings/EarningsPage"), (m) => m.EarningsPage) },
                        { path: "kit", lazy: page(() => import("./pages/kit/KitPage"), (m) => m.KitPage) },
                        { path: "levels", lazy: page(() => import("./pages/levels/LevelsPage"), (m) => m.LevelsPage) },
                        { path: "rules", lazy: page(() => import("./pages/rules/RulesPage"), (m) => m.RulesPage) },
                        { path: "account", lazy: page(() => import("./pages/account/AccountPage"), (m) => m.AccountPage) },
                        { path: "notifications", lazy: page(() => import("./pages/notifications/NotificationsPage"), (m) => m.NotificationsPage) },
                        { path: "glossary", lazy: page(() => import("./pages/glossary/GlossaryPage"), (m) => m.GlossaryPage) },
                        { path: "ambassador", lazy: page(() => import("./pages/ambassador/AmbassadorPage"), (m) => m.AmbassadorPage) },
                        { path: "special", lazy: page(() => import("./pages/special/SpecialPage"), (m) => m.SpecialPage) },
                        { path: "special/campaign", lazy: page(() => import("./pages/special/CampaignPage"), (m) => m.CampaignPage) },
                        { path: "special/turnover", lazy: page(() => import("./pages/special/TurnoverPage"), (m) => m.TurnoverPage) },
                        // The pack moved to «بازاریابی با کد تخفیف» (2026-10-28); old links and notifications land there.
                        { path: "special/pack", element: <Navigate to="/codes?type=pack" replace /> },
                        {
                            path: "*",
                            element: (
                                <RouteStub
                                    title="صفحه پیدا نشد"
                                    description="آدرسی که باز کردید در پنل بازاریاب وجود ندارد."
                                    icon={SearchLg}
                                    emptyTitle="این صفحه وجود ندارد"
                                    emptyDescription="شاید آدرس اشتباه تایپ شده یا صفحه جابه‌جا شده است."
                                    points={[]}
                                    action={{ label: "بازگشت به خانه", href: "/" }}
                                />
                            ),
                        },
                    ],
                },
            ],
        },
    ],
    { basename: import.meta.env.BASE_URL.replace(/\/$/, "") || "/" },
);
